import React, { useState } from 'react';
import { Modal, Button, Input, message, Space, Tooltip } from 'antd';
import { FileTextOutlined, CopyOutlined, SwapOutlined } from '@ant-design/icons';
import { copyToClipboard } from '../../utils/clipboard';

const { TextArea } = Input;

const BatchFastaConverter: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');

  // 显示模态框
  const showModal = () => {
    setIsModalVisible(true);
  };

  // 关闭模态框
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // 复制结果到剪贴板
  const handleCopy = () => {
    if (outputText) {
      if (copyToClipboard(outputText)) {
        message.success('已复制到剪贴板');
      } else {
        message.error('复制失败，请手动复制');
      }
    }
  };

  // 清除序列中的非法字符
  const cleanSequence = (sequence: string): string => {
    // 移除引号
    let cleanedSeq = sequence.replace(/["']/g, '');
    // 只保留ATCG和氨基酸字符，去除空格、数字和其他非法字符
    return cleanedSeq.replace(/[^ACDEFGHIKLMNPQRSTVWYacdefghiklmnpqrstvwy]/g, '');
  };

  // 转换为FASTA格式
  const convertToFasta = () => {
    if (!inputText.trim()) {
      message.warning('请输入数据');
      return;
    }

    try {
      const result: string[] = [];
      
      // 预处理输入文本，将换行替换为空格
      let processedText = inputText;
      
      // 找出所有引号内的内容，并临时替换换行符
      const quotedRegex = /"([^"]*)"|'([^']*)'/g;
      const quotedParts: {original: string, processed: string}[] = [];
      
      processedText = processedText.replace(quotedRegex, (match, p1, p2) => {
        const content = p1 || p2;
        // 替换引号内的换行为特殊标记
        const processedContent = content.replace(/\r?\n/g, '###NEWLINE###');
        const processedMatch = match.replace(content, processedContent);
        quotedParts.push({
          original: match,
          processed: processedMatch
        });
        return processedMatch;
      });
      
      // 分割输入文本为行
      const lines = processedText.trim().split(/\r?\n/);
      
      // 检查是否只有一列数据
      const singleColumn = lines.every(line => !line.includes('\t'));
      
      if (singleColumn) {
        // 如果只有一列，将每行作为序列
        let sequenceCount = 0;
        
        lines.forEach((line) => {
          // 恢复特殊标记为原始内容
          let processedLine = line;
          quotedParts.forEach(part => {
            processedLine = processedLine.replace(part.processed, part.original);
          });
          
          // 处理引号内的内容
          const quotedContent = processedLine.match(/"([^"]*)"|'([^']*)'/);
          let sequence = processedLine.trim();
          
          if (quotedContent) {
            // 如果行中包含引号内容，提取引号内的序列
            sequence = (quotedContent[1] || quotedContent[2]).replace(/###NEWLINE###/g, '');
          } else {
            // 如果没有引号，直接使用整行
            sequence = processedLine.trim();
          }
          
          if (sequence) {
            sequenceCount++;
            const cleanedSequence = cleanSequence(sequence);
            const prefix = cleanedSequence.substring(0, Math.min(5, cleanedSequence.length));
            const id = `${sequenceCount}_${prefix}...`;
            result.push(`>${id}`);
            result.push(cleanedSequence);
          }
        });
      } else {
        // 处理两列数据（ID和序列）
        const dataEntries: { id: string, sequence: string }[] = [];
        
        // 首先解析所有行，将相关的行分组
        let currentId = '';
        let currentSequence = '';
        
        for (let i = 0; i < lines.length; i++) {
          // 恢复特殊标记为原始内容
          let processedLine = lines[i];
          quotedParts.forEach(part => {
            processedLine = processedLine.replace(part.processed, part.original);
          });
          
          const line = processedLine.trim();
          if (!line) continue;
          
          const parts = line.split('\t');
          
          if (parts.length >= 2) {
            // 如果已经有ID和序列，保存之前的条目
            if (currentId) {
              dataEntries.push({
                id: currentId,
                sequence: currentSequence
              });
            }
            
            // 设置新的ID和序列
            currentId = parts[0].trim();
            
            // 处理序列部分的引号
            let sequence = parts[1].trim();
            const quotedContent = sequence.match(/"([^"]*)"|'([^']*)'/);
            
            if (quotedContent) {
              // 如果序列包含引号内容，提取引号内的序列
              sequence = (quotedContent[1] || quotedContent[2]).replace(/###NEWLINE###/g, '');
            }
            
            currentSequence = sequence;
          } else if (currentId) {
            // 如果只有一列且已有ID，则认为是序列的延续（处理单元格换行）
            // 处理可能包含引号的情况
            let additionalSeq = line;
            const quotedContent = additionalSeq.match(/"([^"]*)"|'([^']*)'/);
            
            if (quotedContent) {
              // 如果包含引号内容，提取引号内的序列
              additionalSeq = (quotedContent[1] || quotedContent[2]).replace(/###NEWLINE###/g, '');
            }
            
            // 直接合并到当前序列中，不添加任何分隔符
            currentSequence += additionalSeq;
          }
        }
        
        // 添加最后一个条目
        if (currentId) {
          dataEntries.push({
            id: currentId,
            sequence: currentSequence
          });
        }
        
        // 生成FASTA格式
        dataEntries.forEach((entry) => {
          const cleanedSequence = cleanSequence(entry.sequence);
          result.push(`>${entry.id}`);
          result.push(cleanedSequence);
        });
      }
      
      setOutputText(result.join('\n'));
    } catch (error) {
      console.error('转换出错:', error);
      message.error('转换出错，请检查输入格式');
    }
  };

  // 使用说明文本
  const instructionsText = `使用说明:
1. 从Excel表格复制两列数据（第一列为ID，第二列为序列）粘贴到此处
2. 如果只复制一列数据，ID将自动生成为"序号_前五个字符..."
3. 如果序列包含在引号内（如"ATCG..."），即使被换行也会被视为一条完整序列
4. 系统会自动过滤序列中的非法字符和引号`;

  return (
    <>
      <Button onClick={showModal} icon={<FileTextOutlined />}>
        批量转FASTA
      </Button>

      <Modal
        title="批量转FASTA"
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            关闭
          </Button>,
          <Button key="convert" onClick={convertToFasta}>
            转换
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <span style={{fontWeight: 'bold'}}>输入数据：</span>
            <TextArea
              rows={10}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={instructionsText}
              style={{ marginTop: 8 }}
            />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
            <Button 
              icon={<SwapOutlined />} 
              onClick={convertToFasta}
              size="large"
            >
              转换为FASTA格式
            </Button>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{fontWeight: 'bold'}}>FASTA格式结果：</span>
              <Tooltip title="复制到剪贴板">
                <Button 
                  icon={<CopyOutlined />} 
                  onClick={handleCopy}
                  disabled={!outputText}
                >
                  复制结果
                </Button>
              </Tooltip>
            </div>
            <TextArea
              rows={10}
              value={outputText}
              readOnly
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default BatchFastaConverter;